import React, { useMemo } from "react";
import { gql, useQuery } from "urql";
import { Link } from "wouter";
import { useQueryState } from "../../../components/src/use-query-state";
import { cloneDeep, sortBy } from "lodash";

import { defaultDate, DateForm } from "../../../components/src/dateForm";
import { dateToYYYYMMDDString } from "../../../components/src/common/Utils";

import { Page } from "../../../components/src/Page";
import { ReactTable } from "../../../components/src/react-table/ReactTable";
import { SelectColumnFilter, SelectNotZeroColumnFilter, notEquals } from "../../../components/src/react-table/ReactTableFilters";
import { GET_ME } from "../common/queries";
import { PermissionAssetEnum } from "../types.generated";

const GET_FUND_UNITS = gql`
    query GetFundUnits($date: GraphQLDateString!) {
        fundParties: parties(filter: { typeIn: [Fund] }) {
            _id
            name
            fundInfo {
                fundType
                classes {
                    mainClass
                    enabled
                    instrumentId
                }
            }
        }

        FundUnits: reconcileFundUnits(date: $date, filterOutZeroDiff: false) {
            reportedNbr
            reportedId
            calculatedNbr
            shareRegisterNbr
            unitsAfterOrders
            isin
            instrumentId
            instrument {
                name
            }
            diff
            shareRegisterDiff
            reporterId
            reporter {
                name
            }
            comment
        }
    }
`;

const ReconciliationDataGrid = ({ endDate }) => {
    const [{ fetching, error, data }] = useQuery({
        query: GET_FUND_UNITS,
        variables: { date: endDate },
        requestPolicy: "network-only"
    });

    const columns = useMemo(
        () => [
            {
                header: "Diffs",
                id: "diffs",
                accessorFn: (row) => (row.diff || row.shareRegisterDiff ? "⚠️" : "✅"),
                cell: (cellProps) => <div style={{ textAlign: "center" }}>{cellProps.getValue()}</div>,
                filter: SelectColumnFilter,
                size: 60
            },
            {
                header: "Calculated nbr",
                accessorKey: "calculatedNbr",
                filterFn: "startsWith",
                cell: (cellProps) => <div style={{ textAlign: "right" }}>{cellProps.getValue()}</div>,
                size: 110
            },
            {
                header: "Reported nbr",
                accessorKey: "reportedNbr",
                filterFn: "startsWith",
                cell: (cellProps) => {
                    const value = <div style={{ textAlign: "right" }}>{cellProps.getValue()}</div>;
                    const reportedId = cellProps.row.original.reportedId;
                    if (reportedId) {
                        return <Link to={`/backoffice/totalunits/${reportedId}`}>{value}</Link>;
                    }
                    return value;
                },
                size: 110
            },
            {
                header: "Share register nbr",
                accessorKey: "shareRegisterNbr",
                filterFn: "startsWith",
                cell: (cellProps) => <div style={{ textAlign: "right" }}>{cellProps.getValue()}</div>,
                size: 120
            },
            {
                header: "Units after orders",
                accessorKey: "unitsAfterOrders",
                filterFn: "startsWith",
                cell: (cellProps) => <div style={{ textAlign: "right" }}>{cellProps.getValue()}</div>,
                size: 120
            },
            {
                header: "ISIN",
                accessorKey: "isin",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return <Link to={`/instruments/${row.original.instrumentId}`}>{cellProps.getValue()}</Link>;
                },
                filter: SelectColumnFilter,
                size: 130
            },
            {
                header: "Name",
                id: "instrument.name",
                accessorKey: "instrument.name",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    if (row.original.instrument && row.original.instrument.name) {
                        return <Link to={`/instruments/${row.original.instrumentId}`}>{row.original.instrument.name}</Link>;
                    }
                    return null;
                },
                filter: SelectColumnFilter,
                size: 200
            },
            {
                header: "Reporter name",
                id: "reporter.name",
                accessorKey: "reporter.name",
                accessorFn: (row) => (row.reporter ? row.reporter.name : null),
                cell: (cellProps) => {
                    const { row } = cellProps;
                    if (row.original.reporter) {
                        return <Link to={`/parties/${row.original.reporterId}`}>{row.original.reporter.name}</Link>;
                    }
                    return null;
                },
                filter: SelectColumnFilter,
                size: 180
            },
            {
                header: "Diff",
                accessorKey: "diff",
                filter: SelectNotZeroColumnFilter,
                filterFn: notEquals,
                cell: (cellProps) => <div style={{ textAlign: "right" }}>{cellProps.getValue()}</div>,
                size: 100
            },
            {
                header: "Share register diff",
                accessorKey: "shareRegisterDiff",
                filter: SelectNotZeroColumnFilter,
                filterFn: notEquals,
                cell: (cellProps) => <div style={{ textAlign: "right" }}>{cellProps.getValue()}</div>,
                size: 120
            },
            {
                header: "Comment",
                accessorKey: "comment",
                filter: SelectColumnFilter
            }
        ],
        []
    );

    if (fetching)
        return (
            <Page>
                <div>
                    <div>Fetching reconciliation</div>
                    <div>Loading</div>
                </div>
            </Page>
        );
    if (error) return <div>`Error! ${error.message}`</div>;

    const enabledInstrumentIds = new Set<string>();
    (data.fundParties || []).forEach((party) => {
        (party.fundInfo?.classes || []).forEach((cls) => {
            if (cls.enabled && cls.instrumentId) {
                enabledInstrumentIds.add(cls.instrumentId);
            }
        });
    });

    const fundUnits = sortBy(
        cloneDeep(data.FundUnits).filter((item) => item.isin && enabledInstrumentIds.has(item.instrumentId)),
        "instrument.name"
    );

    return (
        <div>
            <p>Number of items: {fundUnits.length}</p>
            <ReactTable columns={columns} data={fundUnits} defaultHiddenColumns={[]} exportToXlsxFile="Reconciliation.xlsx" />
        </div>
    );
};

export const ReconciliationPage = (): React.ReactElement => {
    const [endDate] = useQueryState("endDate", dateToYYYYMMDDString(defaultDate()));

    const [{ fetching: loadingMe, error: errorMe, data: data }] = useQuery({
        query: GET_ME,
        requestPolicy: "network-only"
    });

    if (loadingMe) return <div>Loading...</div>;

    if (errorMe)
        return (
            <div>
                <p>Error:</p>
                <pre> {JSON.stringify(errorMe, null, 2)}</pre>
            </div>
        );

    return (
        <div>
            <div className="flex flex-wrap -mx-2">
                <div className="w-full px-2 mb-4 text-right">
                    {data &&
                    data.me &&
                    data.me.frontendRole &&
                    data.me.frontendRole.assets &&
                    (data.me.frontendRole.assets.includes(PermissionAssetEnum.BackOffice) || data.me.frontendRole.assets.length === 0) ? (
                        <a
                            href="https://github.com/CaptorAB/documents/blob/master/BackOffice/manual/units_reconciliation.md"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Manual
                        </a>
                    ) : null}
                </div>
                <div className="w-full sm:w-3/12 lg:w-2/12 px-2 mb-4">
                    <DateForm defaultDateString={endDate} dateName={"endDate"}></DateForm>
                </div>
            </div>

            <div className="flex flex-wrap -mx-2">
                <div className="w-full px-2 mb-4">
                    <ReconciliationDataGrid endDate={endDate} />
                </div>
            </div>
        </div>
    );
};
